/**
 * API Key Encryption Utilities
 *
 * Encrypts/decrypts API keys using a key derived from the JWT token.
 * This prevents plain-text API keys from being visible in devtools.
 *
 * Security notes:
 * - Uses AES-GCM encryption
 * - Encryption key is derived from JWT token using PBKDF2
 * - Even if encrypted key is intercepted, it requires the JWT token to decrypt
 */

// Get crypto API (works in both Node.js and browsers)
function getCrypto(): Crypto {
  if (typeof globalThis.crypto !== 'undefined') {
    return globalThis.crypto
  }
  if (typeof window !== 'undefined' && window.crypto) {
    return window.crypto
  }
  throw new Error('Crypto API not available')
}

// Base64 encoding/decoding helpers (works in both Node.js and browsers)
function encodeBase64(data: Uint8Array): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(data).toString('base64')
  }
  return btoa(String.fromCharCode.apply(null, Array.from(data)))
}

function decodeBase64(str: string): Uint8Array {
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(str, 'base64'))
  }
  const binary = atob(str)
  return new Uint8Array(binary.length).map((_, i) => binary.charCodeAt(i))
}

/**
 * Derive an encryption key from a JWT token using PBKDF2
 */
async function deriveKeyFromToken(
  token: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const crypto = getCrypto()
  // Import the token as a key material
  const encoder = new TextEncoder()
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(token),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  )

  // Derive a key using PBKDF2
  // Create a new Uint8Array to ensure proper BufferSource type
  const saltBuffer = new Uint8Array(salt)
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * Encrypt an API key using a JWT token
 * @param apiKey - The API key to encrypt
 * @param token - The JWT token to derive encryption key from
 * @returns Base64-encoded encrypted data with IV and salt
 */
export async function encryptApiKey(
  apiKey: string,
  token: string
): Promise<string> {
  const crypto = getCrypto()
  // Generate random salt and IV
  const saltBuffer = new Uint8Array(16)
  const ivBuffer = new Uint8Array(12)
  const salt = crypto.getRandomValues(saltBuffer)
  const iv = crypto.getRandomValues(ivBuffer)

  // Derive key from token
  const key = await deriveKeyFromToken(token, salt)

  // Encrypt the API key
  const encoder = new TextEncoder()
  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    key,
    encoder.encode(apiKey)
  )

  // Combine salt + iv + encrypted data
  const combined = new Uint8Array(
    salt.length + iv.length + encrypted.byteLength
  )
  combined.set(salt, 0)
  combined.set(iv, salt.length)
  combined.set(new Uint8Array(encrypted), salt.length + iv.length)

  // Return as base64
  return encodeBase64(combined)
}

/**
 * Decrypt an API key using a JWT token
 * @param encryptedData - Base64-encoded encrypted data
 * @param token - The JWT token to derive decryption key from
 * @returns The decrypted API key
 */
export async function decryptApiKey(
  encryptedData: string,
  token: string
): Promise<string> {
  const crypto = getCrypto()
  // Decode base64
  const combined = decodeBase64(encryptedData)

  // Extract salt, IV, and encrypted data
  const salt = combined.slice(0, 16)
  const iv = combined.slice(16, 28)
  const encrypted = combined.slice(28)

  // Derive key from token
  const key = await deriveKeyFromToken(token, salt)

  // Decrypt
  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    key,
    encrypted
  )

  // Convert to string
  const decoder = new TextDecoder()
  return decoder.decode(decrypted)
}
